import { useCallback, useState } from "react";
import { Button, Checkbox, Collapse, Group, Select, Text } from "@mantine/core";
import { IconEdit } from "@tabler/icons-react";
import { FormField } from "@benchmark/ui/components/atomic";
import { FormGroup } from "@benchmark/ui/components/atomic";
import { FormSection } from "@benchmark/ui/components/atomic";
import { TrimmedTextInput as TextInput } from "@benchmark/ui/components/atomic";
import {
  PlacesAutocomplete,
  type PlaceResult,
} from "@/components/common/forms/PlacesAutocomplete";
import { getStateOptions } from "@benchmark/core/constants";
import type { UseFormReturnType } from "@mantine/form";
import type { PatientFormData } from "@benchmark/core/types";

/**
 * Props for AddressSection component.
 * @agentic @benchmark/ui
 */
type AddressSectionProps = {
  form: UseFormReturnType<PatientFormData>;
};

/** Whether any address detail field has data. */
const hasAddressDetails = (values: PatientFormData): boolean =>
  Boolean(
    values.street ||
    values["street-2"] ||
    values.city ||
    values.state ||
    values.zip,
  );

/**
 * Patient mailing address with Google Places autocomplete and progressive disclosure.
 * Detail fields (line 2, city, state, ZIP) stay hidden until an address is selected
 * or the user opts to enter manually. "Address Not Available" bypasses validation.
 * @agentic @benchmark/ui
 */
export const AddressSection = ({ form }: AddressSectionProps) => {
  const addressNotAvailable = form.values["address-not-available"] === true;
  const [manualEntry, setManualEntry] = useState(false);

  const detailsVisible =
    !addressNotAvailable && (hasAddressDetails(form.values) || manualEntry);

  const handlePlaceSelect = useCallback(
    (place: PlaceResult) => {
      form.setFieldValue(
        "street",
        place.street || place.formattedAddress || "",
      );
      form.setFieldValue("city", place.city || "");
      form.setFieldValue("state", place.stateCode || "");
      form.setFieldValue("zip", place.postalCode || "");
    },
    [form],
  );

  const handleAddressNotAvailableChange = useCallback(
    (checked: boolean) => {
      form.setFieldValue("address-not-available", checked);
      if (checked) {
        form.setFieldValue("street", "");
        form.setFieldValue("street-2", "");
        form.setFieldValue("city", "");
        form.setFieldValue("state", "");
        form.setFieldValue("zip", "");
        setManualEntry(false);
      }
    },
    [form],
  );

  return (
    <FormSection title="Mailing Address">
      <FormField label="Address">
        <PlacesAutocomplete
          value={form.values.street || ""}
          onChange={(value) => form.setFieldValue("street", value)}
          onPlaceSelect={handlePlaceSelect}
          placeholder="Search for an address..."
          disabled={addressNotAvailable}
          error={form.errors.street as string | undefined}
        />
      </FormField>

      <Collapse in={detailsVisible}>
        <FormField label="Address Line 2">
          <TextInput
            placeholder="Apt, suite, unit, floor, etc."
            autoComplete="off"
            disabled={addressNotAvailable}
            {...form.getInputProps("street-2")}
          />
        </FormField>

        <FormGroup>
          <FormField label="City">
            <TextInput
              placeholder="Enter city"
              autoComplete="off"
              disabled={addressNotAvailable}
              {...form.getInputProps("city")}
            />
          </FormField>
          <FormField label="State">
            <Select
              placeholder="Select state"
              data={getStateOptions()}
              searchable
              clearable
              disabled={addressNotAvailable}
              {...form.getInputProps("state")}
            />
          </FormField>
          <FormField label="ZIP Code" required={!addressNotAvailable}>
            <TextInput
              placeholder="Enter ZIP code"
              autoComplete="off"
              disabled={addressNotAvailable}
              {...form.getInputProps("zip")}
            />
          </FormField>
        </FormGroup>
      </Collapse>

      <Group justify="space-between" mt="xs">
        <Checkbox
          label="Address Not Available"
          checked={addressNotAvailable}
          onChange={(e) =>
            handleAddressNotAvailableChange(e.currentTarget.checked)
          }
        />
        {!addressNotAvailable && !detailsVisible && (
          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconEdit size={14} />}
            onClick={() => setManualEntry(true)}
          >
            <Text size="xs">Enter address manually</Text>
          </Button>
        )}
      </Group>
    </FormSection>
  );
};
